import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "../ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Separator } from "../ui/separator";
import { Search, X, Filter, RotateCcw } from "lucide-react";
import { Badge } from "../ui/badge";
import { CategoryTag } from "../../api/categoryTags";

interface Vendor {
  _id: string;
  displayName: string;
}

interface FilterBarProps {
  onFilterChange: (filters: {
    search: string;
    categories: string[];
    tags: string[];
    difficulty: string[];
    estimatedTimeMin: number | undefined;
    estimatedTimeMax: number| undefined;
    priceMin: number| undefined;
    priceMax: number| undefined;
    vendorId: string| undefined;
  }) => void;
  availableCategories: CategoryTag[];
  availableTags: CategoryTag[];
  availableVendors?: Vendor[];
}

const FilterBar = ({
  onFilterChange,
  availableCategories,
  availableTags,
  availableVendors = [],
}: FilterBarProps) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<string>("");
  const [timeRange, setTimeRange] = useState<string>("");
  const [priceRange, setPriceRange] = useState<string>("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isInitialMount = useRef(true);

  // Helper to parse time and price ranges
  const getTimeRange = (range: string): { min?: number; max?: number } => {
    switch (range) {
      case "0-30":
        return { max: 30 };
      case "30-60":
        return { min: 30, max: 60 };
      case "60-120":
        return { min: 60, max: 120 };
      case "120+":
        return { min: 120 };
      default:
        return {};
    }
  };

  const getPriceRange = (range: string): { min?: number; max?: number } => {
    switch (range) {
      case "free":
        return { max: 0 };
      case "0-10":
        return { min: 0.01, max: 10 };
      case "10-25":
        return { min: 10, max: 25 };
      case "25-50":
        return { min: 25, max: 50 };
      case "50+":
        return { min: 50 };
      default:
        return {};
    }
  };

  // Debounce search input only
  useEffect(() => {
    // Skip debounced call on initial mount (immediate effect will handle it)
    if (isInitialMount.current) {
      return;
    }

    const timeValues = getTimeRange(timeRange);
    const priceValues = getPriceRange(priceRange);

    const timer = setTimeout(() => {
      onFilterChange({
        search,
        categories: selectedCategories,
        tags: selectedTags,
        difficulty: selectedDifficulty,
        vendorId: selectedVendor || undefined,
        estimatedTimeMin: timeValues.min,
        estimatedTimeMax: timeValues.max,
        priceMin: priceValues.min,
        priceMax: priceValues.max,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Trigger immediately when filters change, or on initial mount
  useEffect(() => {
    const timeValues = getTimeRange(timeRange);
    const priceValues = getPriceRange(priceRange);

    onFilterChange({
      search,
      categories: selectedCategories,
      tags: selectedTags,
      difficulty: selectedDifficulty,
      vendorId: selectedVendor || undefined,
      estimatedTimeMin: timeValues.min,
      estimatedTimeMax: timeValues.max,
      priceMin: priceValues.min,
      priceMax: priceValues.max,
    });

    // After initial mount, allow debounced effect to run
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [selectedCategories, selectedTags, selectedDifficulty, selectedVendor, timeRange, priceRange]);

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((c) => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleTagToggle = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const handleDifficultyToggle = (difficulty: string) => {
    setSelectedDifficulty((prev) =>
      prev.includes(difficulty)
        ? prev.filter((d) => d !== difficulty)
        : [...prev, difficulty]
    );
  };

  // Helper to get localized name based on current language
  const getLocalizedName = (name: any): string => {
    if (typeof name === 'string') return name; // Legacy string format
    if (typeof name === 'object' && name !== null) {
      // Multi-language format
      const currentLang = i18n.language || 'en';
      if (currentLang === 'he' && name.he) return name.he;
      if (currentLang === 'de' && name.de) return name.de;
      return name.en || name.he || name.de || '';
    }
    return '';
  };

  // Helper to get contrast text color
  const getContrastTextColor = (hexColor: string): string => {
    if (!hexColor) return "#000000";
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  };

  const handleClearFilters = () => {
    setSearch("");
    setSelectedCategories([]);
    setSelectedTags([]);
    setSelectedDifficulty([]);
    setSelectedVendor("");
    setTimeRange("");
    setPriceRange("");
  };

  const activeFilterCount =
    selectedCategories.length +
    selectedTags.length +
    selectedDifficulty.length +
    (selectedVendor ? 1 : 0) +
    (timeRange ? 1 : 0) +
    (priceRange ? 1 : 0);

  return (
    <div className="mb-6 space-y-3">
      {/* Search and Filter Controls */}
      <div className="flex gap-2 items-center">
        {/* Search Input */}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={t("search_placeholder") || "Search products..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-8"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Single Filter Button - Both Mobile and Desktop */}
        <div>
          <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2 relative">
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">{t("filters") || "Filters"}</span>
                {activeFilterCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 h-5 w-5 rounded-full p-0 text-xs flex justify-center items-center"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[90vw] sm:w-[600px] md:w-[700px] p-0 max-h-[70vh] flex flex-col" align="end">
              {/* Fixed Header */}
              <div className="p-4 pb-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm">{t("filter_options") || "Filters"}</h4>
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                      className="h-8 px-2 text-xs"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      {t("clear_all") || "Clear"}
                    </Button>
                  )}
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1 p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    {/* Categories */}
                    {availableCategories.length > 0 && (
                      <div className="space-y-2">
                        <div className="font-medium text-sm">{t("categories") || "Categories"}</div>
                        <div className="flex flex-wrap gap-2">
                          {availableCategories.map((category) => (
                            <Button
                              key={category._id}
                              variant={selectedCategories.includes(category._id) ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleCategoryToggle(category._id)}
                              className="h-8 text-xs"
                            >
                              <div
                                className="w-2 h-2 rounded-full mr-1.5"
                                style={{ backgroundColor: category.color || "#6B7280" }}
                              />
                              {getLocalizedName(category.name)}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {availableTags.length > 0 && (
                      <div className="space-y-2">
                        <div className="font-medium text-sm">{t("tags") || "Tags"}</div>
                        <div className="flex flex-wrap gap-2">
                          {availableTags.map((tag) => (
                            <Button
                              key={tag._id}
                              variant={selectedTags.includes(tag._id) ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleTagToggle(tag._id)}
                              className="h-8 text-xs"
                            >
                              <div
                                className="w-2 h-2 rounded-full mr-1.5"
                                style={{ backgroundColor: tag.color || "#F3F4F6" }}
                              />
                              {getLocalizedName(tag.name)}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Vendor */}
                    <div className="space-y-2">
                      <div className="font-medium text-sm">{t("vendor") || "Vendor"}</div>
                      {availableVendors.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {availableVendors.map((vendor) => (
                            <Button
                              key={vendor._id}
                              variant={selectedVendor === vendor._id ? "default" : "outline"}
                              size="sm"
                              onClick={() => setSelectedVendor(vendor._id)}
                              className="h-8 text-xs"
                            >
                              {vendor.displayName}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 py-2">
                          {t("no_vendors") || "No vendors available"}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    {/* Difficulty */}
                    <div className="space-y-2">
                      <div className="font-medium text-sm">{t("difficulty") || "Difficulty"}</div>
                      <div className="grid grid-cols-2 gap-2">
                        {["beginner", "intermediate", "advanced"].map((level) => (
                          <Button
                            key={level}
                            variant={selectedDifficulty.includes(level) ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleDifficultyToggle(level)}
                            className="h-8 text-xs"
                          >
                            {t(level) || level.charAt(0).toUpperCase() + level.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Time Range */}
                    <div className="space-y-2">
                      <div className="font-medium text-sm">{t("time") || "Time"}</div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: "0-30", label: t("under_30_min") || "< 30 min" },
                          { value: "30-60", label: t("30_60_min") || "30-60 min" },
                          { value: "60-120", label: t("1_2_hours") || "1-2 hrs" },
                          { value: "120+", label: t("over_2_hours") || "2+ hrs" },
                        ].map((option) => (
                          <Button
                            key={option.value}
                            variant={timeRange === option.value ? "default" : "outline"}
                            size="sm"
                            onClick={() => setTimeRange(option.value)}
                            className="h-8 text-xs"
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Price Range */}
                    <div className="space-y-2">
                      <div className="font-medium text-sm">{t("price") || "Price"}</div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: "free", label: t("free") || "Free" },
                          { value: "0-10", label: "$0-$10" },
                          { value: "10-25", label: "$10-$25" },
                          { value: "25-50", label: "$25-$50" },
                          { value: "50+", label: "$50+" },
                        ].map((option) => (
                          <Button
                            key={option.value}
                            variant={priceRange === option.value ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPriceRange(option.value)}
                            className="h-8 text-xs"
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Clear Filters Button */}
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <X className="h-4 w-4 mr-1" />
            {t("clear_filters") || "Clear"} ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Active Filter Tags */}
      {(selectedCategories.length > 0 ||
        selectedTags.length > 0 ||
        selectedDifficulty.length > 0 ||
        selectedVendor ||
        timeRange ||
        priceRange) && (
        <div className="flex flex-wrap gap-2">
          {selectedCategories.map((categoryId) => {
            const category = availableCategories.find((c) => c._id === categoryId);
            if (!category) return null;
            return (
              <Badge
                key={`cat-${categoryId}`}
                className="gap-1 pl-2 pr-1 py-1"
                style={{
                  backgroundColor: category.color || "#6B7280",
                  color: category.textColor || getContrastTextColor(category.color || "#6B7280"),
                }}
              >
                {getLocalizedName(category.name)}
                <button
                  onClick={() => handleCategoryToggle(categoryId)}
                  className="hover:opacity-80 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
          {selectedTags.map((tagId) => {
            const tag = availableTags.find((t) => t._id === tagId);
            if (!tag) return null;
            return (
              <span
                key={`tag-${tagId}`}
                className="gap-1 pl-2 pr-1 py-1 rounded inline-flex items-center text-xs"
                style={{
                  backgroundColor: tag.color || "#F3F4F6",
                  color: tag.textColor || getContrastTextColor(tag.color || "#F3F4F6"),
                }}
              >
                {getLocalizedName(tag.name)}
                <button
                  onClick={() => handleTagToggle(tagId)}
                  className="hover:opacity-80 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          {selectedDifficulty.map((level) => (
            <Badge key={`diff-${level}`} className="gap-1 pl-2 pr-1 py-1">
              {t(level) || level.charAt(0).toUpperCase() + level.slice(1)}
              <button
                onClick={() => handleDifficultyToggle(level)}
                className="hover:opacity-80 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {selectedVendor && (
            <Badge key="vendor-badge" className="gap-1 pl-2 pr-1 py-1">
              {availableVendors.find((v) => v._id === selectedVendor)?.displayName || "Vendor"}
              <button
                onClick={() => setSelectedVendor("")}
                className="hover:opacity-80 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {timeRange && (
            <Badge key="time-badge" className="gap-1 pl-2 pr-1 py-1">
              {timeRange === "0-30" && (t("under_30_min") || "Under 30 min")}
              {timeRange === "30-60" && (t("30_60_min") || "30-60 min")}
              {timeRange === "60-120" && (t("1_2_hours") || "1-2 hours")}
              {timeRange === "120+" && (t("over_2_hours") || "Over 2 hours")}
              <button
                onClick={() => setTimeRange("")}
                className="hover:opacity-80 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {priceRange && (
            <Badge key="price-badge" className="gap-1 pl-2 pr-1 py-1">
              {priceRange === "free" && (t("free") || "Free")}
              {priceRange === "0-10" && "$0-$10"}
              {priceRange === "10-25" && "$10-$25"}
              {priceRange === "25-50" && "$25-$50"}
              {priceRange === "50+" && "$50+"}
              <button
                onClick={() => setPriceRange("")}
                className="hover:opacity-80 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
